import { DateService, PulseEnvironment } from '@pulse/browser-shared';
import { sleep } from '@pulse/common';
import { Subject } from 'rxjs';
import { safeMeasurableTypes } from '../measurable-types';
import { RequestStats } from '../request-stats';
import { parseContentTypeHeader, requestURL } from '../utils';

type FetchFn = (input: RequestInfo, init?: RequestInit) => Promise<Response>;

export class FetchCollectorService {
    private reportStream = new Subject<RequestStats>();
    private patched = false;

    constructor(
        private date: DateService,
        private env: PulseEnvironment,
    ) {}

    requests() {
        this.patchFetch();

        return this.reportStream.asObservable();
    }

    supported() {
        return (
            'ReadableStream' in this.env.win &&
            'ArrayBuffer' in this.env.win &&
            'Request' in this.env.win &&
            'Response' in this.env.win &&
            typeof this.env.win.fetch === 'function'
        );
    }

    private patchFetch() {
        if (this.patched || !this.supported()) {
            return;
        }

        const context = this.env.win;
        const original = context.fetch.bind(context);
        const wrappedFetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> =>
            this.monitoredFetch(input, init, original);

        this.env.win.fetch = wrappedFetch;
        this.patched = true;
    }

    private async monitoredFetch(
        input: RequestInfo,
        init: RequestInit | undefined,
        originalFetch: FetchFn,
    ): Promise<Response> {
        const report: RequestStats = {
            url: '',
            method: '',
            start: 0,
            fetchEnd: 0,
            end: 0,
            responseStatus: 0,
            initiator: 'fetch',
        };

        let response: Response | undefined = undefined;
        let responseClone: Response | undefined = undefined;

        try {
            const request = new Request(input, init);
            report.url = requestURL(request.url, this.env) || '';
            report.method = request.method.toUpperCase();
            request.signal?.addEventListener('abort', () => {
                report.aborted = true;
            });

            //
            // Make the actual request
            //////////////////////////////////
            report.start = this.date.hrt();
            response = await originalFetch(request);
            report.fetchEnd = this.date.hrt();
            //////////////////////////////////

            try {
                report.responseBodySize = response.headers.has('content-length')
                    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      Number(response.headers.get('content-length')!)
                    : undefined;

                const bodyAccessible =
                    response.type === 'basic' ||
                    response.type === 'cors' ||
                    response.type === 'default';

                if (
                    bodyAccessible &&
                    report.responseContentType &&
                    !Number.isSafeInteger(report.responseBodySize) &&
                    safeMeasurableTypes.has(report.responseContentType)
                ) {
                    responseClone = response.clone();
                }
                // eslint-disable-next-line no-empty
            } catch {}

            return response;
        } finally {
            report.fetchEnd = response ? report.fetchEnd : this.date.hrt();
            sleep(0)
                .then(() => this.reportResponse(response, responseClone, report))
                .catch((err) => console.error(err));
        }
    }

    private async reportResponse(
        response?: Response,
        responseClone?: Response,
        report?: RequestStats,
    ) {
        if (!report) {
            return;
        }

        if (response) {
            try {
                const contentTypeHeader = response.headers?.get('content-type');
                report.responseContentType = parseContentTypeHeader(contentTypeHeader);
                report.redirected = response.redirected;
                report.responseMode = response.type;
                report.responseStatus = response.status;

                if (!Number.isSafeInteger(report.responseBodySize) && responseClone) {
                    try {
                        const body = await responseClone.blob();
                        report.responseBodySize = body.size;
                        // eslint-disable-next-line no-empty
                    } catch {}
                }

                // eslint-disable-next-line no-empty
            } catch {}
        }

        report.end = this.date.hrt();
        this.reportStream.next(report);
    }
}
